import { ApprovalMode } from "./approvalModes";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  ToolErrorKind,
  classifyToolFailure,
  createClientTool,
  toolError,
  toolSuccess,
} from "./ClientTools";
import {
  collectionMemberIds,
  collectionMissingIds,
  resourceIdPrefixList,
  resourceNounListPlural,
  toResourceRefs,
} from "./collectionResourceIds";
import {
  MAX_PROPOSED_IDS,
  RESOURCE_TYPES,
  coerceCreateCollectionInput,
  describeCollectionFailure,
  isCreateCollectionParams,
  type CreateCollectionParams,
} from "./collectionToolInput";
import { CreateCollectionConfirmation } from "./CreateCollectionConfirmation";

const inputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    name: {
      type: "string",
      description: "Short human-readable name, e.g. 'hard landings'.",
    },
    resource_type: {
      type: "string",
      enum: [...RESOURCE_TYPES],
      description:
        "What the collection holds. A collection is homogeneous and its type " +
        "is fixed at creation, so every member added later must be of this " +
        "kind. Required even when 'resources' is given.",
    },
    description: {
      type: "string",
      description: "Optional longer description of what the collection is for.",
    },
    resources: {
      type: "array",
      items: { type: "string" },
      maxItems: MAX_PROPOSED_IDS,
      description:
        "Optional initial member IDs, all of resource_type " +
        `(${resourceIdPrefixList()}). Must be an array even for a single ID, ` +
        `and at most ${MAX_PROPOSED_IDS} long; add the rest afterwards with ` +
        "client_add_to_collection.",
    },
    tags: {
      type: "array",
      items: { type: "string" },
      description: "Optional tags for categorization.",
    },
  },
  required: ["name", "resource_type"],
};

async function execute(
  params: CreateCollectionParams,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  // Not TRANSPORT: nothing was attempted, and no retry can supply what the
  // browser context is missing.
  if (!deps?.createCollection || !deps?.orgId) {
    return toolError(
      "Collections are unavailable in this session, so this tool cannot run. " +
        "Nothing was attempted.",
      {},
      ToolErrorKind.VALIDATION,
    );
  }
  // The same verdict the confirmation modal reached, so the user cannot approve
  // a proposal that is refused here.
  const verdict = coerceCreateCollectionInput(params);
  if (!verdict.ok) {
    return toolError(
      verdict.reason,
      verdict.details ?? {},
      ToolErrorKind.VALIDATION,
    );
  }
  const { name, description, resourceType, tags, resources } = verdict.payload;

  // Only the service call belongs in the transport `try`. A throw from anything
  // after it would otherwise be reported as an unknown outcome even though the
  // collection demonstrably exists.
  let created;
  try {
    created = await deps.createCollection({
      orgId: deps.orgId,
      name,
      resourceType,
      ...(description && { description }),
      ...(resources.length > 0 && {
        resources: toResourceRefs(resources, resourceType),
      }),
      ...(tags.length > 0 && { tags }),
    });
  } catch (err) {
    const kind = classifyToolFailure(err);
    return toolError(
      `Failed to create collection: ${describeCollectionFailure(err)}`,
      kind === ToolErrorKind.TRANSPORT
        ? {
            // The create endpoint takes no idempotency key, and
            // `CollectionService.createCollection` passes `idempotent: false`
            // precisely so a timed-out POST is not replayed. A retry here can
            // produce a second collection.
            retry_safe: false,
            recovery:
              "Do NOT call this tool again: the request may have succeeded " +
              "before failing. Look for the collection first (list_related " +
              "from your org, or get_entity on a cl_ ID) and only create it " +
              "again if it is genuinely absent.",
          }
        : {},
      kind,
    );
  }

  // Report what the server built, not what was asked for: `hydrate_resources`
  // routes unresolvable and cross-org references into `missing` while still
  // creating the collection, so `resources.length` can overstate membership.
  const memberIds = collectionMemberIds(created);
  const missingIds = collectionMissingIds(created);
  return toolSuccess("created_collection", {
    collection_id: created.collection_id,
    collection_name: created.name,
    member_count: memberIds.length,
    resource_type: created.resource_type,
    ...(missingIds.length > 0 && {
      missing_ids: missingIds,
      partial:
        `The collection was created, but ${missingIds.length} of the ` +
        "requested members could not be resolved and are not in it.",
    }),
  });
}

export const tool = createClientTool<CreateCollectionParams>({
  name: "client_create_collection",
  description:
    "Create a new Roboto collection — a named, curated set of datasets, " +
    "files, or events (e.g. 'confirmed hard landings'). A collection holds " +
    "exactly one kind of resource, fixed at creation by resource_type, so " +
    "choose it to match what the user wants to gather. Initial members are " +
    "optional; an empty collection can be filled later with " +
    "client_add_to_collection.",
  input_schema: inputSchema,
  validateInput: isCreateCollectionParams,
  execute,
  defaultApprovalMode: ApprovalMode.REQUIRE_APPROVAL,
  // Approval-only, like client_add_to_collection: creating a collection is a
  // durable, user-visible artifact and resource_type cannot be changed after.
  allowableApprovalModes: [ApprovalMode.REQUIRE_APPROVAL],
  displayName: "Create Collection",
  displayDescription: `Creates a new collection of ${resourceNounListPlural()}.`,
  ConfirmationContent: CreateCollectionConfirmation,
});
